import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { getKey } from "../helpers";
import { API_BASE_URL, buildApiUrl } from "../helpers/base_url";

const service = axios.create({
  baseURL: API_BASE_URL,
});
service.defaults.headers.common["Content-Type"] = "application/json";

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Function to refresh the access token (exported for fetch-based callers that bypass axios interceptors)
export const refreshAccessToken = async (): Promise<string | null> => {
  try {
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) {
      return null;
    }

    const response = await axios.post(
      buildApiUrl("/api/auth/refresh"),
      { refreshToken },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    // Handle API response format: { status, message, data: { accessToken, refreshToken, ... } }
    const responseData = response.data;
    const tokenData = responseData?.data;
    
    const newAccess =
      tokenData?.accessToken ||
      tokenData?.token ||
      tokenData?.jwt ||
      responseData?.accessToken ||
      responseData?.token;

    if (newAccess) {
      // Update tokens in localStorage
      localStorage.setItem("accessToken", newAccess);
      
      if (tokenData.refreshToken) {
        localStorage.setItem("refreshToken", tokenData.refreshToken);
      }
      
      if (tokenData.tokenType) {
        localStorage.setItem("tokenType", tokenData.tokenType);
      }
      
      if (tokenData.expiresIn) {
        localStorage.setItem("expiresIn", tokenData.expiresIn.toString());
      }
      
      if (tokenData.user) {
        localStorage.setItem("user", JSON.stringify(tokenData.user));
      }
      
      localStorage.setItem("loginTime", Date.now().toString());

      // Dispatch custom event to notify other parts of the app
      window.dispatchEvent(new Event("localStorageChange"));

      return newAccess;
    }
    return null;
  } catch (error: any) {
    // Refresh failed, clear tokens
    console.error("Token refresh failed:", error);
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
    localStorage.removeItem("expiresIn");
    localStorage.removeItem("loginTime");
    window.dispatchEvent(new Event("localStorageChange"));
    return null;
  }
};

// Add a request interceptor
service.interceptors.request.use(
  async function (config) {
    const raw = await getKey("accessToken");
    if (raw) {
      const token = raw.trim();
      const scheme = ((await getKey("tokenType")) || "Bearer").trim();
      if (/^(bearer|token)\s+/i.test(token)) {
        config.headers["Authorization"] = token;
      } else {
        config.headers["Authorization"] = `${scheme} ${token}`;
      }
    }

    return config;
  },
  function (error) {
    if (error.response) {
      return Promise.reject(error.response);
    } else if (error.request) {
      return Promise.reject(error.request);
    } else {
      return Promise.reject(error.message);
    }
  }
);

// Add a response interceptor
service.interceptors.response.use(
  function (response) {
    return response;
  },
  async function (error: AxiosError) {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Check if error is 401 and we haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers["Authorization"] = `Bearer ${token}`;
            }
            return service(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const newToken = await refreshAccessToken();

      if (newToken) {
        // Token refreshed successfully, update header and retry request
        if (originalRequest.headers) {
          originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
        }
        processQueue(null, newToken);
        isRefreshing = false;
        return service(originalRequest);
      } else {
        // Refresh failed, process queue with error and redirect to login
        processQueue(new Error("Token refresh failed"), null);
        isRefreshing = false;

        // Only redirect to login if user is on a protected route
        const protectedRoutes = [
          "/profile",
          "/engagement-chat",
          "/cart-page",
          "/edit-profile",
        ];
        const currentPath = window.location.pathname;
        const isProtectedRoute = protectedRoutes.some((route) =>
          currentPath.startsWith(route)
        );

        if (isProtectedRoute && !currentPath.includes("/login")) {
          window.location.replace("/login");
        }
      }
    }

    // For non-401 errors or if refresh failed, reject the promise
    if (error.response) {
      return Promise.reject(error.response);
    } else if (error.request) {
      return Promise.reject(error.request);
    } else {
      return Promise.reject(error.message);
    }
  }
);

export const get = async (url: string) => {
  try {
    const response = await service.get(url);
    return response.data;
  } catch (error) {
    handleRequestError(error);
    throw error; // Throwing error allows React Query to handle it properly
  }
};

/**
 * GET without sending Content-Type. Some gateways reject GET requests that carry
 * the default axios `application/json` Content-Type header.
 */
export const apiGet = async (url: string) => {
  try {
    const response = await service.request({
      method: "GET",
      url,
      transformRequest: [
        (_data, headers) => {
          delete headers["Content-Type"];
          return _data;
        },
      ],
    });
    return response.data;
  } catch (error) {
    handleRequestError(error);
    throw error;
  }
};

export const post = async (url: string, payload: any) => {
  try {
    const response = await service.post(url, payload);
    return response.data;
  } catch (error) {
    handleRequestError(error);
    throw error;
  }
};

export const patch = async (url: string, payload: any) => {
  try {
    const response = await service.patch(url, payload);
    return response.data;
  } catch (error) {
    handleRequestError(error);
    throw error;
  }
};

export const put = async (url: string, payload: any) => {
  try {
    const response = await service.put(url, payload);
    return response.data;
  } catch (error) {
    handleRequestError(error);
    throw error;
  }
};

export const Delete = async (url: string) => {
  try {
    const response = await service.delete(url);
    return response.data;
  } catch (error) {
    handleRequestError(error);
    throw error;
  }
};

const handleRequestError = (error: any) => {
  // 401 errors are now handled by the interceptor with automatic token refresh
  // This function is kept for any additional error handling if needed
  // The interceptor will automatically refresh tokens and retry requests
  // Only redirects to login if refresh fails and user is on a protected route
};
